import React, { useEffect, useRef } from "react";
import Backbone from "backbone";
import App from "app";
import ProgressView from "app/components/flow/designer/progressView";
import { useAppDispatch, useAppSelector } from "src/stores/redux/hooks";
import { getAppModel, getAppStatus, getShowProgressView } from "../helpers/selectors";
import {
    setShowProgressViewAction,
    setIsTableProgressModalOpenAction
} from "src/stores/redux/features/flowdesigner/flowdesigner.slice";

const OK_STATUSES = ["RUNNING", "QUIESCING", "STOPPING", "QUIESCED", "STOPPED", "COMPLETED"];

const ProgressViewWrapper: React.FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const viewRef = useRef<any>(null);
    const dispatch = useAppDispatch();
    const app = useAppSelector(getAppModel);
    const appStatus = useAppSelector(getAppStatus);
    const showProgressView = useAppSelector(getShowProgressView);

    useEffect(() => {
        const shouldShowProgress = appStatus ? OK_STATUSES.includes(appStatus.toUpperCase()) : false;
        dispatch(setShowProgressViewAction(shouldShowProgress));
    }, [appStatus, dispatch]);

    useEffect(() => {
        // Adds listener to open table progress
        const openTableProgress = () => dispatch(setIsTableProgressModalOpenAction(true));
        App.vent.on("show:table:progress:view", openTableProgress);
        return () => App.vent.off("show:table:progress:view", openTableProgress);
    }, [dispatch]);

    useEffect(() => {
        if (!containerRef.current || !app || !showProgressView) {
            return;
        }

        try {
            // Create a model structure that matches exactly what the original designer uses
            // Looking at designer.js line 1105: this.model.app = this.model.datasource.app;
            // This means app is a direct property, not a Backbone model attribute

            const model = new Backbone.Model() as any;
            model.app = app; // Set app as a direct property, not an attribute

            // Now that we've fixed the legacy code, we can use a simple, clean approach
            // Create the ProgressView directly without workarounds

            const progressView = new (ProgressView.LiteView as any)({
                model: model
            }) as any;

            // Store reference for cleanup
            viewRef.current = progressView;

            // Check if container and view element are available
            if (containerRef.current && progressView.el) {
                containerRef.current.appendChild(progressView.el);

                // Render the view
                if (progressView.render && typeof progressView.render === "function") {
                    progressView.render();
                }

                // Trigger onShow to initialize the view properly
                if (progressView.onShow && typeof progressView.onShow === "function") {
                    progressView.onShow();
                }
            } else {
                console.error("Container or progress view element not available");
            }
        } catch (error) {
            console.error("Error creating progress view:", error);
        }

        // Cleanup function
        return () => {
            if (viewRef.current) {
                try {
                    if (viewRef.current.destroy) {
                        viewRef.current.destroy();
                    } else if (viewRef.current.remove) {
                        viewRef.current.remove();
                    }
                } catch (error) {
                    console.warn("Error destroying progress view:", error);
                }
                viewRef.current = null;
            }
        };
    }, [app, appStatus, showProgressView]);

    // Update app status when it changes
    useEffect(() => {
        if (viewRef.current && viewRef.current.toggleElementsEnabled && appStatus) {
            viewRef.current.toggleElementsEnabled(appStatus.toUpperCase(), false);
        }
    }, [appStatus]);

    if (!showProgressView) return null;
    return <div ref={containerRef} />;
};

export default ProgressViewWrapper;
